import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchChaptersByBook } from "../../redux/reducers/chaptersSlice";
import { fetchBooks } from "../../redux/reducers/booksSlice";
import VersesDisplay from "./VersesDisplay";
import { motion, AnimatePresence } from "framer-motion";
import { saveLastRead } from "utils/lastRead";

export const manualScrollRef = { current: false };

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function BookChapters() {
  const { bookId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const query = useQuery();
  const dispatch = useDispatch();
  const versesRef = useRef(null);
  const [animationDirection, setAnimationDirection] = useState(1);

  const emptyArray = useMemo(() => [], []);

  const books = useSelector((state) => state.books.data || emptyArray);
  const chapters = useSelector(
    (state) => state.chapters?.chaptersByBook?.[bookId] || emptyArray
  );
  const chapterStatus = useSelector(
    (state) => state.chapters?.statusByBook?.[bookId] || "idle"
  );

  const [selectedChapter, setSelectedChapter] = useState(null);
  // store a pending chapter number requested via query param until chapters are loaded
  const [pendingChapterFromQuery, setPendingChapterFromQuery] = useState(null);

  const chaptersExist = useSelector(
    (state) => !!state.chapters.chaptersByBook?.[bookId]?.length
  );

  // Compute book early so callbacks can use it
  const book = books.find((b) => b.id === parseInt(bookId, 10));

  const scrollToVerses = () => {
    manualScrollRef.current = true;
    versesRef.current?.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => {
      manualScrollRef.current = false;
    }, 800);
  };

  // Navigation handlers: set selectedChapter and update URL (but don't save directly)
  const handleNextChapter = useCallback(() => {
    if (!chapters.length || selectedChapter == null) return;
    if (selectedChapter < Math.max(...chapters)) {
      const next = selectedChapter + 1;
      setAnimationDirection(1);
      setSelectedChapter(next);
      setTimeout(scrollToVerses, 100);
      navigate(
        { pathname: `/bible/book/${bookId}`, search: `?chapter=${next}` },
        { replace: true }
      );
    }
  }, [selectedChapter, chapters, bookId, navigate]);

  const handlePreviousChapter = useCallback(() => {
    if (!chapters.length || selectedChapter == null) return;
    if (selectedChapter > 1) {
      const prevChapter = selectedChapter - 1;
      setAnimationDirection(-1);
      setSelectedChapter(prevChapter);
      setTimeout(scrollToVerses, 100);
      navigate(
        {
          pathname: `/bible/book/${bookId}`,
          search: `?chapter=${prevChapter}`,
        },
        { replace: true }
      );
    }
  }, [selectedChapter, bookId, navigate, chapters]);

  const handleChapterClick = (chapter) => {
    setSelectedChapter(chapter);
    setTimeout(scrollToVerses, 100);
    navigate(
      { pathname: `/bible/book/${bookId}`, search: `?chapter=${chapter}` },
      { replace: true }
    );
  };

  // keyboard nav
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        handleNextChapter();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePreviousChapter();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleNextChapter, handlePreviousChapter]);

  useEffect(() => {
    if (books.length === 0 && chapterStatus !== "loading") {
      dispatch(fetchBooks());
    }
  }, [books.length, dispatch, chapterStatus]);

  useEffect(() => {
    setSelectedChapter(null);
    if (!chaptersExist && chapterStatus !== "loading") {
      dispatch(fetchChaptersByBook(bookId));
    }
  }, [bookId, dispatch, chaptersExist, chapterStatus]);

  // When query changes, store the requested chapter as pending.
  useEffect(() => {
    const qChapter = query.get("chapter");
    if (qChapter) {
      const num = Number(qChapter);
      if (!Number.isNaN(num)) {
        setPendingChapterFromQuery(num);
      } else {
        setPendingChapterFromQuery(null);
      }
    } else {
      setPendingChapterFromQuery(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // When chapters are loaded and there's a pending chapter request, apply it.
  useEffect(() => {
    if (pendingChapterFromQuery != null && chaptersExist) {
      // Validate requested chapter is within range
      const maxChapter = Math.max(...chapters);
      const target = Math.min(Math.max(1, pendingChapterFromQuery), maxChapter);
      setSelectedChapter(target);
      setTimeout(scrollToVerses, 150);
      // Save last read now that we have book.name available
      if (book) {
        saveLastRead({
          bookId: Number(bookId),
          bookName: book.name || null,
          chapter: target,
        });
      }
      // clear pending
      setPendingChapterFromQuery(null);
    }
  }, [pendingChapterFromQuery, chaptersExist, chapters, book, bookId]);

  // === Persist any manual selectedChapter changes once the book exists ===
  useEffect(() => {
    if (selectedChapter != null && book) {
      saveLastRead({
        bookId: Number(bookId),
        bookName: book.name || null,
        chapter: selectedChapter,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChapter, book]);

  if (chapterStatus === "loading") {
    return <p className="text-center mt-10">Cargando capitulos...</p>;
  }

  if (!books.length) {
    return <p className="text-center mt-10">Cargando libros...</p>;
  }

  if (!book) {
    return <p className="text-center mt-10">Libro no encontrado</p>;
  }

  function AnimatedVersesDisplay({ bookId, chapter, direction }) {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={chapter}
          initial={{ opacity: 0, x: 30 * direction }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 * direction }}
          transition={{ duration: 1.4, ease: "easeInOut" }}
          className="w-full flex justify-center"
        >
          <VersesDisplay
            bookId={bookId}
            chapter={chapter}
            persistLastRead={true}
          />
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <div className="app-background font-bembo p-6 mt-20 flex flex-col md:flex-row">
      {/* Left Sidebar - Capítulos */}
      <div className="w-full md:w-64 p-4 flex flex-col items-start space-y-3 border-r border-slate-300/30">
        <Link
          to="/biblia"
          className="text-slate-600 hover:text-slate-900 underline"
        >
          ← Volver a libros
        </Link>
        <br />
        <h3 className="text-xl font-semibold text-neutral-700">Capítulos</h3>
        <div className="flex flex-col space-y-2 w-full max-h-[500px] overflow-y-auto pr-2">
          {chapters.map((chapter) => (
            <button
              key={chapter}
              type="button"
              id={`chapter-${chapter}`}
              onClick={() => handleChapterClick(chapter)}
              className={`py-2 rounded-xl shadow-sm transition text-left w-full pl-3
              ${
                selectedChapter === chapter
                  ? "bg-black text-white"
                  : "bg-white text-black hover:bg-slate-100"
              }`}
            >
              Capítulo {chapter}
            </button>
          ))}
        </div>
      </div>

      {/* Main Book Section */}
      <div className="flex-1 flex flex-col items-center text-center p-4">
        <h2 className="title text-5xl">{book.name}</h2>
        {book.description && (
          <p className="description mt-4 max-w-2xl leading-relaxed">
            {book.description}
          </p>
        )}

        {selectedChapter && (
          <>
            {/* TOP Navigation Buttons */}
            <div className="mt-4 flex justify-center space-x-4">
              {selectedChapter > 1 && (
                <button
                  type="button"
                  id="btn-prev-top"
                  onClick={handlePreviousChapter}
                  className="bg-white hover:bg-black hover:text-white rounded-full p-2 text-black"
                  title="Capítulo anterior"
                >
                  Capítulo Anterior
                </button>
              )}
              {selectedChapter < Math.max(...chapters) && (
                <button
                  type="button"
                  id="btn-next-top"
                  onClick={handleNextChapter}
                  className="bg-white hover:bg-black hover:text-white rounded-full p-2 text-black"
                  title="Siguiente capítulo"
                >
                  Capítulo Siguiente
                </button>
              )}
            </div>

            <div ref={versesRef} className="mt-6 w-full scroll-mt-24">
              <AnimatedVersesDisplay
                bookId={bookId}
                chapter={selectedChapter}
                direction={animationDirection}
              />
            </div>

            {/* BOTTOM Navigation Buttons */}
            <div className="mt-4 flex justify-center space-x-4">
              {selectedChapter > 1 && (
                <button
                  type="button"
                  id="btn-prev-bottom"
                  onClick={handlePreviousChapter}
                  className="bg-white hover:bg-black hover:text-white rounded-full p-2 text-black"
                  title="Capítulo anterior"
                >
                  Capítulo Anterior
                </button>
              )}
              {selectedChapter < Math.max(...chapters) && (
                <button
                  type="button"
                  id="btn-next-bottom"
                  onClick={handleNextChapter}
                  className="bg-white hover:bg-black hover:text-white rounded-full p-2 text-black"
                  title="Siguiente capítulo"
                >
                  Capítulo Siguiente
                </button>
              )}
            </div>
          </>
        )}
      </div>
      <br />
      <br />
      <br />
    </div>
  );
}

export default BookChapters;
